// ===========================================
// EcheancesList — Prochaines échéances avec onglets 7/15/30 jours
// Actions inline : Voir détail, Renouveler (si permission)
// ===========================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tabs, Empty, Skeleton, Tag, Button } from 'antd';
import { ClockCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatMontant, formatDate } from '../../../common/utils/format';
import Can from '../../../components/can';

const buckets = [
    { key: '7j',  label: '7 jours',  dataKey: 'dans7j'  },
    { key: '15j', label: '15 jours', dataKey: 'dans15j' },
    { key: '30j', label: '30 jours', dataKey: 'dans30j' },
];

const getClientLabel = (client) => {
    if (!client) return '—';
    if (client.raisonSociale) return client.raisonSociale;
    return `${client.prenom || ''} ${client.nom || ''}`.trim() || client.code || '—';
};

const EcheanceItem = ({ contrat }) => {
    const navigate = useNavigate();
    const clientLabel = getClientLabel(contrat.client);
    const typeLabel = contrat.typeAssurance?.nom || '—';
    const dateStr = formatDate(contrat.dateEcheance);

    // Calcul jours restants pour signaler l'urgence
    const now = new Date();
    const echeance = new Date(contrat.dateEcheance);
    const daysLeft = Math.ceil((echeance - now) / (1000 * 60 * 60 * 24));
    const urgent = daysLeft <= 7;

    return (
        <li className={`op-list-item ${urgent ? 'op-list-item-urgent' : ''}`}>
            <div className="op-list-item-main">
                <div className="op-list-item-title">{clientLabel}</div>
                <div className="op-list-item-meta">
                    <Tag color="blue">{typeLabel}</Tag>
                    <span className="op-list-item-numero">N° {contrat.numeroPolice}</span>
                </div>
            </div>
            <div className="op-list-item-mid">
                <div className="op-list-item-date">{dateStr}</div>
                <div className={`op-list-item-days ${urgent ? 'is-urgent' : ''}`}>
                    {daysLeft <= 0 ? 'Expiré' : `Dans ${daysLeft}j`}
                </div>
            </div>
            <div className="op-list-item-amount">
                {formatMontant(contrat.primeTTC || 0)} F
            </div>
            <div className="op-list-item-actions">
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    className="btn-detail echeance-action-detail"
                    onClick={() => navigate(`/dashboard/contrat/${contrat._id}`)}
                    aria-label="Voir détail"
                >
                    Détail
                </Button>
                <Can perm="contrats:renouveler">
                    <Button
                        size="small"
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={() => navigate(`/dashboard/nouveau-contrat?renew=${contrat._id}`)}
                    >
                        Renouveler
                    </Button>
                </Can>
            </div>
        </li>
    );
};

const EcheancesList = ({ data, loading }) => {
    const [activeBucket, setActiveBucket] = useState('7j');

    const tabs = buckets.map((b) => {
        const bucketData = data?.[b.dataKey];
        const count = bucketData?.count || 0;
        return {
            key: b.key,
            label: (
                <span>
                    {b.label}
                    {count > 0 && <span className="op-tab-count">{count}</span>}
                </span>
            ),
            children: (
                <div className="op-list-wrapper">
                    {loading ? (
                        <Skeleton active paragraph={{ rows: 4 }} />
                    ) : !bucketData || bucketData.count === 0 ? (
                        <Empty
                            description="Aucune échéance"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            className="op-empty"
                        />
                    ) : (
                        <ul className="op-list">
                            {bucketData.contrats.slice(0, 8).map((c) => (
                                <EcheanceItem key={c._id} contrat={c} />
                            ))}
                        </ul>
                    )}
                </div>
            ),
        };
    });

    return (
        <Card
            className="dashboard-op-card"
            title={
                <div className="chart-card-title">
                    <ClockCircleOutlined />
                    <span>Prochaines échéances</span>
                </div>
            }
        >
            <Tabs
                activeKey={activeBucket}
                onChange={setActiveBucket}
                items={tabs}
                tabBarStyle={{ marginBottom: 12 }}
                size="small"
            />
        </Card>
    );
};

export default EcheancesList;
